import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Car, LayoutDashboard, CarFront, MessageSquare, LogOut, Menu, X, Sparkles, Bot, CalendarCheck, BarChart3, Share2, TrendingDown, RefreshCw, Megaphone, Download, DollarSign, Facebook, Search, ShoppingCart, Bell, Camera, KeyRound } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, isViewer } = useAuth();

  useEffect(() => {
    const t = () => localStorage.getItem('token');
    if (!t()) return;
    const fetchCounts = () => {
      axios.get(`${API}/admin/notifications/counts`, { headers: { Authorization: `Bearer ${t()}` } })
        .then((r) => setNotifCount(r.data.total || 0))
        .catch(() => {});
    };
    fetchCounts();
    const iv = setInterval(fetchCounts, 60000);
    return () => clearInterval(iv);
  }, []);

  // PWA Install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Redirect viewers to vehicles page if they try to access other pages
  useEffect(() => {
    if (isViewer() && location.pathname !== '/admin/vehicles') {
      navigate('/admin/vehicles');
    }
  }, [location.pathname, isViewer, navigate]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowInstallButton(false);
    }
    setDeferredPrompt(null);
  };

  // All nav items with admin-only flag
  const allNavItems = [
    { href: '/admin', label: 'Dashboard', icon: LayoutDashboard, adminOnly: true },
    { href: '/admin/vehicles', label: 'Vehicles', icon: CarFront, adminOnly: false },
    { href: '/admin/carvana', label: 'Carvana Ops', icon: ShoppingCart, adminOnly: true, badge: true },
    { href: '/admin/sync', label: 'Synchronisation', icon: RefreshCw, adminOnly: true },
    { href: '/admin/pricing', label: 'Tarification', icon: TrendingDown, adminOnly: true },
    { href: '/admin/test-drives', label: 'Essais Routiers', icon: CalendarCheck, adminOnly: true },
    { href: '/admin/appraisals', label: 'Évaluations', icon: DollarSign, adminOnly: true },
    { href: '/admin/fb-marketplace', label: 'FB Marketplace', icon: Facebook, adminOnly: true },
    { href: '/admin/fb-posts', label: 'Publications FB', icon: Megaphone, adminOnly: true },
    { href: '/admin/exports', label: 'Plateformes', icon: Share2, adminOnly: true },
    { href: '/admin/analytics', label: 'Analytiques', icon: BarChart3, adminOnly: true },
    { href: '/admin/marketing', label: 'Marketing', icon: Sparkles, adminOnly: true },
    { href: '/admin/promos', label: 'Promotions', icon: Megaphone, adminOnly: true },
    { href: '/admin/customer-photos', label: 'Photos clients', icon: Camera, adminOnly: true },
    { href: '/admin/access-codes', label: "Codes d'accès", icon: KeyRound, adminOnly: true },
    { href: '/admin/chatbot', label: 'Assistant IA', icon: Bot, adminOnly: true },
    { href: '/admin/inquiries', label: 'Inquiries', icon: MessageSquare, adminOnly: true },
    { href: '/admin/sem', label: 'SEO / SEM', icon: Search, adminOnly: true },
  ];

  // Filter nav items based on role
  const navItems = isViewer() 
    ? allNavItems.filter(item => !item.adminOnly)
    : allNavItems;

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-56 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 lg:static ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-slate-200">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://res.cloudinary.com/dtqyyb6su/image/upload/v1774460834/entrepot/assets/company_logo_footer.png" 
                alt="Entrepôt Auto Montréal" 
                className="h-8 w-auto"
              />
              <span className="font-bold text-xs text-slate-800" style={{ fontFamily: "'Montserrat', sans-serif" }}>Entrepôt Auto</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-0.5">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                  isActive(item.href)
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
                {item.badge && notifCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center" data-testid="sidebar-notif-badge">
                    {notifCount}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* User Section */}
          <div className="p-3 border-t border-slate-200">
            <div className="flex items-center gap-2 px-3 py-2 mb-1">
              <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-medium">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <p className="text-xs font-medium text-slate-800 truncate">{user?.name}</p>
                  {isViewer() && (
                    <span className="px-1.5 py-0.5 text-[9px] font-medium bg-amber-100 text-amber-700 rounded">
                      Lecture
                    </span>
                  )}
                  {isAdmin() && (
                    <span className="px-1.5 py-0.5 text-[9px] font-medium bg-green-100 text-green-700 rounded">
                      Admin
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            {showInstallButton && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-xs h-8 mb-1"
                onClick={handleInstallClick}
                data-testid="admin-install-button"
              >
                <Download className="h-3.5 w-3.5 mr-2" />
                Installer l'app
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-slate-500 hover:text-slate-900 hover:bg-slate-100 text-xs h-8"
              onClick={handleLogout}
              data-testid="admin-logout-button"
            >
              <LogOut className="h-3.5 w-3.5 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 lg:px-6 h-12 flex items-center justify-between" style={{ paddingTop: 'env(safe-area-inset-top, 0px)', height: 'calc(3rem + env(safe-area-inset-top, 0px))' }}>
          <button
            className="lg:hidden p-2 text-slate-500 hover:text-slate-900"
            onClick={() => setSidebarOpen(true)}
            data-testid="admin-mobile-menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-4">
            <Link 
              to="/" 
              className="text-xs text-slate-500 hover:text-blue-600 transition-colors"
              data-testid="admin-view-site"
            >
              View Site →
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};
